import { useEffect } from "react";

const SITE_URL = "https://www.joinindexed.com";
const DEFAULT_OG_IMAGE = "https://www.joinindexed.com/images/og-default.png";

export type JsonLdSchema =
  | { "@type": "Organization"; [key: string]: unknown }
  | { "@type": "WebSite"; [key: string]: unknown }
  | { "@type": "Article"; [key: string]: unknown }
  | { "@type": "BlogPosting"; [key: string]: unknown }
  | { "@type": "BreadcrumbList"; [key: string]: unknown }
  | { "@type": "Service"; [key: string]: unknown }
  | { "@type": "FAQPage"; [key: string]: unknown }
  | { "@type": "LocalBusiness"; [key: string]: unknown }
  | { "@type": "Person"; [key: string]: unknown }
  | { "@type": "ProfilePage"; [key: string]: unknown }
  | { "@type": "CollectionPage"; [key: string]: unknown }
  | { "@type": "WebPage"; [key: string]: unknown }
  | { [key: string]: unknown };

interface SeoMetaOptions {
  /** Browser tab title — keep ≤65 chars */
  title: string;
  /** Meta description — 70–155 chars */
  description: string;
  /** Canonical path, e.g. "/about" — will be prefixed with SITE_URL */
  canonical?: string;
  /** OG image URL — defaults to site-wide OG image */
  ogImage?: string;
  /** OG type — defaults to "website" */
  ogType?: "website" | "article" | "profile";
  /** JSON-LD structured data schema objects */
  jsonLd?: JsonLdSchema | JsonLdSchema[];
  /** If true, adds <meta name="robots" content="noindex, nofollow"> */
  noIndex?: boolean;
}

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(name: string, attr: "name" | "property" = "name") {
  const el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (el) el.remove();
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(schemas: JsonLdSchema[]) {
  // Remove any existing JSON-LD scripts injected by this hook
  document.querySelectorAll('script[data-seo-hook="true"]').forEach((el) => el.remove());
  schemas.forEach((schema) => {
    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.setAttribute("data-seo-hook", "true");
    script.textContent = JSON.stringify({ "@context": "https://schema.org", ...schema });
    document.head.appendChild(script);
  });
}

/**
 * useSeoMeta — sets per-page SEO metadata:
 *   • <title>
 *   • <meta name="description">
 *   • Open Graph tags (og:title, og:description, og:image, og:type, og:url)
 *   • Twitter card tags (twitter:card, twitter:title, twitter:description, twitter:image)
 *   • <link rel="canonical">
 *   • JSON-LD structured data (one or more schemas)
 *   • Optional <meta name="robots" content="noindex, nofollow">
 */
export function useSeoMeta({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  jsonLd,
  noIndex = false,
}: SeoMetaOptions) {
  useEffect(() => {
    // ── Title ────────────────────────────────────────────────────────────────
    document.title = title;

    // ── Description ──────────────────────────────────────────────────────────
    setMeta("description", description);

    // ── Robots ────────────────────────────────────────────────────────────────────
    if (noIndex) {
      setMeta("robots", "noindex, nofollow");
    } else {
      // Explicitly signal indexability to all crawlers
      setMeta("robots", "index, follow");
    }

    // ── Open Graph ───────────────────────────────────────────────────────────
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:image:width", "1200", "property");
    setMeta("og:image:height", "630", "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:site_name", "Indexed", "property");

    // ── Canonical ────────────────────────────────────────────────────────────
    // Use explicit canonical if provided, otherwise fall back to current path
    const canonicalPath = canonical ?? window.location.pathname;
    setLink("canonical", `${SITE_URL}${canonicalPath}`);
    setMeta("og:url", `${SITE_URL}${canonicalPath}`, "property");

    // ── Twitter card ────────────────────────────────────────────────────────────
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:site", "@Indexed_Agency");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    // ── JSON-LD ──────────────────────────────────────────────────────────────
    if (jsonLd) {
      setJsonLd(Array.isArray(jsonLd) ? jsonLd : [jsonLd]);
    } else {
      // Clean up any leftover JSON-LD from a previous page
      document.querySelectorAll('script[data-seo-hook="true"]').forEach((el) => el.remove());
    }

    return () => {
      document.title = "Indexed | Full-Stack SEO Agency";
      document.querySelectorAll('script[data-seo-hook="true"]').forEach((el) => el.remove());
      removeMeta("robots");
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, ogImage, ogType, noIndex]);
}
